import React from 'react';
import { FadeInUp, SectionLabel, AccentLine, StaggerContainer, StaggerItem } from './AnimationWrappers';
import { Heart, Star, GraduationCap, Stethoscope, UtensilsCrossed, Baby } from 'lucide-react';

const PeopleSection = () => {
  return (
    <>
      {/* === People First Section (Cream bg) === */}
      <section id="people" data-testid="people-section" className="relative overflow-hidden" style={{ background: '#FAF8F5', padding: '70px 0 0' }}>
        <div className="container-main">
          <SectionLabel number="10" title="People First" />
          <FadeInUp>
            <AccentLine />
          </FadeInUp>

          <FadeInUp>
            <h2 className="font-display mb-6" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 700, color: '#1C1C1E' }}>
              Behind Every Stitch, a <span style={{ color: '#C8102E' }}>Person.</span>
            </h2>
          </FadeInUp>
          <FadeInUp delay={0.1}>
            <p className="font-body mb-12" style={{ fontSize: '1rem', color: '#3A3A3C', lineHeight: 1.8, maxWidth: '680px' }}>
              We don't have employees. We have families. The precision in our garments comes from people who feel valued, respected, and invested in. At Qualiance, building a great workplace isn't a CSR initiative — it's a manufacturing strategy.
            </p>
          </FadeInUp>

          {/* === Philosophy Block — Dark card with quote + Diwali image === */}
          <FadeInUp delay={0.15}>
            <div className="overflow-hidden mb-14" style={{ borderRadius: '14px', background: '#1C1C1E' }}>
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="flex flex-col justify-center" style={{ padding: 'clamp(1.5rem, 4vw, 3rem)' }}>
                  <blockquote className="font-display text-white mb-6" style={{
                    fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)', fontWeight: 600, lineHeight: 1.35,
                    borderLeft: '3px solid #C8102E', paddingLeft: '20px',
                  }}>
                    A worker who celebrates at the factory will take pride in what they build at the factory. We don't separate culture from production — they are the same thing.
                  </blockquote>
                  <span className="font-mono" style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                    The Qualiance Philosophy
                  </span>
                </div>
                <div className="relative overflow-hidden" style={{ minHeight: '300px' }}>
                  <img src="/people-diwali.jpg" alt="Qualiance team at Diwali celebration" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </FadeInUp>

          {/* === How We Invest — 4 initiative cards === */}
          <FadeInUp delay={0.2}>
            <h3 className="font-display mb-8" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1C1C1E' }}>
              How We Invest in Our People
            </h3>
          </FadeInUp>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
            {/* Healthcare */}
            <StaggerItem>
              <div data-testid="people-initiative-0" className="card-light h-full" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <div className="overflow-hidden" style={{ minHeight: '200px' }}>
                    <img src="/people-medical-camp.jpg" alt="On-site medical camp — blood pressure check and health screening" className="w-full h-full object-cover" />
                  </div>
                  <div style={{ padding: '1.5rem' }}>
                    <Stethoscope size={20} color="#C8102E" className="mb-3" />
                    <h4 className="font-display mb-2" style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1C1C1E' }}>Healthcare That Comes to You</h4>
                    <p className="font-body" style={{ fontSize: '0.82rem', color: '#6B6B6F', lineHeight: 1.65 }}>
                      Regular on-site medical camps with qualified doctors and nursing staff. Blood pressure checks, general health screenings, and medications — right on the factory floor. No one misses a checkup because they can't afford to miss a shift.
                    </p>
                  </div>
                </div>
              </div>
            </StaggerItem>

            {/* Medical team */}
            <StaggerItem>
              <div data-testid="people-initiative-1" className="card-light h-full" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <div className="overflow-hidden" style={{ minHeight: '200px' }}>
                    <img src="/people-medical-team.jpg" alt="Chithra Charitable Trust medical team partnering with Qualiance" className="w-full h-full object-cover" />
                  </div>
                  <div style={{ padding: '1.5rem' }}>
                    <Heart size={20} color="#C8102E" className="mb-3" />
                    <h4 className="font-display mb-2" style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1C1C1E' }}>Partnered Medical Outreach</h4>
                    <p className="font-body" style={{ fontSize: '0.82rem', color: '#6B6B6F', lineHeight: 1.65 }}>
                      We partner with local charitable trusts and medical institutions to organise free health camps for our workforce. Doctor consultations, diagnostic screenings, and wellness awareness — because a healthy team builds better garments.
                    </p>
                  </div>
                </div>
              </div>
            </StaggerItem>
          </StaggerContainer>

          {/* Row 2: 3 cards */}
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-5 pb-14">
            {/* Festival */}
            <StaggerItem>
              <div data-testid="people-initiative-2" className="card-light h-full" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="overflow-hidden" style={{ height: '220px' }}>
                  <img src="/people-lucky-draw.jpg" alt="Diwali lucky draw — employee receiving appliance prize" className="w-full h-full object-cover" />
                </div>
                <div style={{ padding: '1.3rem' }}>
                  <Star size={18} color="#C8102E" className="mb-2" />
                  <h4 className="font-display mb-1.5" style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1C1C1E' }}>Every Festival, One Family</h4>
                  <p className="font-body" style={{ fontSize: '0.78rem', color: '#6B6B6F', lineHeight: 1.6 }}>
                    Diwali dinners with lucky draws where workers take home appliances. Pongal celebrations with kolams and shared meals. We don't just acknowledge festivals — we make them memorable.
                  </p>
                </div>
              </div>
            </StaggerItem>

            {/* Pongal */}
            <StaggerItem>
              <div data-testid="people-initiative-3" className="card-light h-full" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="overflow-hidden" style={{ height: '220px' }}>
                  <img src="/people-pongal.jpg" alt="Pongal celebration — team gathered around traditional kolam rangoli" className="w-full h-full object-cover" />
                </div>
                <div style={{ padding: '1.3rem' }}>
                  <Star size={18} color="#C8102E" className="mb-2" />
                  <h4 className="font-display mb-1.5" style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1C1C1E' }}>Traditions We Share</h4>
                  <p className="font-body" style={{ fontSize: '0.78rem', color: '#6B6B6F', lineHeight: 1.6 }}>
                    Pongal at Qualiance means hand-drawn kolams on the factory floor, sugarcane decorations, and the entire team — management and operators — standing together as one family.
                  </p>
                </div>
              </div>
            </StaggerItem>

            {/* Next Generation */}
            <StaggerItem>
              <div data-testid="people-initiative-4" className="card-light h-full" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="overflow-hidden" style={{ height: '220px' }}>
                  <img src="/people-college-visit.jpg" alt="KCG College students on industrial visit" className="w-full h-full object-cover" />
                </div>
                <div style={{ padding: '1.3rem' }}>
                  <GraduationCap size={18} color="#C8102E" className="mb-2" />
                  <h4 className="font-display mb-1.5" style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1C1C1E' }}>Growing the Next Generation</h4>
                  <p className="font-body" style={{ fontSize: '0.78rem', color: '#6B6B6F', lineHeight: 1.6 }}>
                    Industrial visits from colleges like KCG College of Technology. Internships, mentorship, and hands-on exposure to technical garment manufacturing — building a pipeline of future industry talent.
                  </p>
                </div>
              </div>
            </StaggerItem>
          </StaggerContainer>


          {/* === Workplace Amenities — Dining + Creche === */}
          <FadeInUp delay={0.25}>
            <h3 className="font-display mb-8" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1C1C1E' }}>
              A Workplace, Not Just a Worksite
            </h3>
          </FadeInUp>

          <FadeInUp delay={0.3}>
            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-5 mb-5 items-start">
              {/* Rooftop Dining — large card */}
              <div className="card-light overflow-hidden" style={{ padding: 0 }}>
                <div className="grid grid-cols-1 md:grid-cols-2" style={{ minHeight: '280px' }}>
                  <div className="overflow-hidden h-full">
                    <img src="/people-dining.jpg" alt="Rooftop dining hall under solar panels — seating for 400+ workers" className="w-full h-full object-cover" style={{ minHeight: '260px' }} />
                  </div>
                  <div className="flex flex-col justify-center" style={{ padding: '1.5rem' }}>
                    <UtensilsCrossed size={20} color="#C8102E" className="mb-3" />
                    <h4 className="font-display mb-2" style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1C1C1E' }}>Rooftop Dining Hall</h4>
                    <p className="font-body" style={{ fontSize: '0.85rem', color: '#6B6B6F', lineHeight: 1.65 }}>
                      A dedicated dining space for our entire workforce — right under the solar panels on the rooftop. Subsidised meals, panoramic views of Tiruppur, and a place to recharge during breaks. When you feed people well, they build better.
                    </p>
                  </div>
                </div>
              </div>

              {/* Creche */}
              <div className="card-light overflow-hidden" style={{ padding: 0 }}>
                <div className="overflow-hidden" style={{ height: '200px' }}>
                  <img src="/people-creche.jpg" alt="On-site creche and daycare facility with toys and play area" className="w-full h-full object-cover" />
                </div>
                <div style={{ padding: '1.3rem' }}>
                  <Baby size={20} color="#C8102E" className="mb-3" />
                  <h4 className="font-display mb-2" style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1C1C1E' }}>On-Site Creche & Daycare</h4>
                  <p className="font-body" style={{ fontSize: '0.82rem', color: '#6B6B6F', lineHeight: 1.65 }}>
                    A safe, fully-equipped childcare facility inside the factory premises. Play mats, toys, educational materials, and supervised care — so parents on the production floor never have to worry about their young children.
                  </p>
                </div>
              </div>
            </div>
          </FadeInUp>

          {/* Terrace garden image — full width atmospheric */}
          <FadeInUp delay={0.3}>
            <div className="overflow-hidden mb-14" style={{ borderRadius: '14px', height: '220px' }}>
              <img src="/people-dining-terrace.jpg" alt="Rooftop terrace garden with seating, plants, and panoramic views" className="w-full h-full object-cover" style={{ objectPosition: 'center 40%' }} />
            </div>
          </FadeInUp>


          {/* Facilitation card — wide */}
          <FadeInUp delay={0.2}>
            <div className="card-light overflow-hidden mb-16" style={{ padding: 0 }}>
              <div className="grid grid-cols-1 md:grid-cols-[0.4fr_1fr]">
                <div className="overflow-hidden" style={{ minHeight: '220px' }}>
                  <img src="/people-facilitation.jpg" alt="Employee's child being felicitated for academic achievement" className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-col justify-center" style={{ padding: '2rem' }}>
                  <Heart size={20} color="#C8102E" className="mb-3" />
                  <h4 className="font-display mb-2" style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1C1C1E' }}>Families, Not Just Workers</h4>
                  <p className="font-body" style={{ fontSize: '0.88rem', color: '#6B6B6F', lineHeight: 1.7, maxWidth: '550px' }}>
                    We felicitate the children of our team members for academic achievements. Scholarships, recognition ceremonies, and a clear message: your family's growth matters to us as much as your work on the line. When a parent sees their child recognised at the workplace, that bond between person and factory becomes unbreakable.
                  </p>
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>
        <div className="section-number">10</div>
      </section>

      {/* === Team Spirit Section (Navy gradient bg) === */}
      <section data-testid="team-spirit-section" className="relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #1A3A5C 0%, #0F2640 100%)', padding: '80px 0',
      }}>
        <div className="container-main relative z-10 text-center">
          <FadeInUp>
            <div className="flex items-center justify-center gap-3 mb-6">
              <span style={{ width: '30px', height: '1px', background: 'rgba(255,255,255,0.3)' }} />
              <span className="font-mono" style={{ fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.25em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>Beyond the Factory Floor</span>
              <span style={{ width: '30px', height: '1px', background: 'rgba(255,255,255,0.3)' }} />
            </div>
          </FadeInUp>

          <FadeInUp delay={0.1}>
            <h2 className="font-display text-white mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, lineHeight: 1.15 }}>
              We Don't Just Build <span style={{ color: '#C8102E' }}>Garments.</span><br />
              We Build <span style={{ color: '#C8102E' }}>Teams.</span>
            </h2>
          </FadeInUp>

          <FadeInUp delay={0.2}>
            <p className="font-body mx-auto mb-12" style={{ fontSize: '1.05rem', fontWeight: 300, color: 'rgba(255,255,255,0.65)', maxWidth: '600px', lineHeight: 1.7 }}>
              The same discipline, coordination, and hunger to win that powers our production lines — shows up on the pitch every weekend.
            </p>
          </FadeInUp>

          <FadeInUp delay={0.3}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-left">
              {/* Card 1: Team Qualiance — 400 hands */}
              <div data-testid="team-spirit-card-0" style={{
                background: 'rgba(255,255,255,0.05)', borderRadius: '14px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <div className="relative" style={{ height: '280px' }}>
                  <img src="/team-400-hands.jpg" alt="Team Qualiance — full squad in blue jerseys outside the factory" className="w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(15,38,64,0.85) 0%, transparent 50%)' }} />
                  <span className="absolute bottom-16 left-5 font-mono text-white px-3 py-1.5 rounded" style={{ background: '#C8102E', fontSize: '0.55rem', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600 }}>Team Qualiance</span>
                  <div className="absolute bottom-4 left-5 right-5">
                    <h3 className="font-display text-white" style={{ fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.2 }}>400 Hands on the Line.<br />11 on the Pitch.</h3>
                  </div>
                </div>
                <div style={{ padding: '1.2rem 1.5rem 1.5rem' }}>
                  <p className="font-body" style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.65 }}>
                    Our cricket squad competes across Tiruppur's inter-industry circuit. Same jersey. Same fire. Same refusal to settle for second.
                  </p>
                </div>
              </div>

              {/* Card 2: NIFT-TEA Champions */}
              <div data-testid="team-spirit-card-1" style={{
                background: 'rgba(255,255,255,0.05)', borderRadius: '14px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <div className="relative" style={{ height: '280px' }}>
                  <img src="/team-nift-tea.jpg" alt="NIFT-TEA Inter-Industry cricket champions with trophy" className="w-full h-full object-cover" />
                </div>
                <div style={{ padding: '1.5rem' }}>
                  <h3 className="font-display text-white mb-3" style={{ fontSize: '1.3rem', fontWeight: 700 }}>NIFT-TEA Inter-Industry Champions</h3>
                  <p className="font-body mb-4" style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.65 }}>
                    The trophy came home. Not because we had the best players — but because this team knows what it means to show up, every single day, and deliver under pressure.
                  </p>
                  <span className="font-mono inline-block" style={{
                    fontSize: '0.55rem', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600,
                    color: '#B87333', border: '1px solid #B87333', borderRadius: '4px', padding: '6px 14px',
                  }}>Championship Winners</span>
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>
    </>
  );
};

export default PeopleSection;
